<template>
    <div
        ref="stoneDetails"
        tabindex="0"
        class="stone-details"
    >
        <div class="photo-container">
            <div
                v-if="image"
                class="photo-wrapper"
            >
                <img
                    :alt="image.alt"
                    :src="image.url"
                    class="image"
                    loading="eager"
                />
            </div>

            <p
                v-if="image && showPhotoText"
                class="photo-disclaimer"
            >
                {{
                    image.sample
                        ? t('diamond_search.sample_photo_disclaimer_text')
                        : t('diamond_search.actual_photo_text')
                }}
            </p>

            <a
                v-if="v360Url"
                :href="v360Url"
                class="video-link"
                rel="nofollow"
                target="_blank"
                :aria-label="`${t('diamond_search.view_video')} - opens in a new tab`"
            >
                {{ t('diamond_search.view_video') }}
            </a>
        </div>

        <div class="details-container">
            <div class="stone-parameters">
                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { type StoneImage } from '@/domain/stone/useStoneImage'

defineProps<{
    showPhotoText?: boolean | false
    image:
        | (Partial<StoneImage> & {
              alt: string
          })
        | undefined
    v360Url?: string | null
}>()

const stoneDetailsEl = useTemplateRef('stoneDetails')
onMounted(() => {
    stoneDetailsEl.value?.focus()
})

const { t } = useI18n()
</script>

<style lang="scss" scoped>
.stone-details {
    width: 100%;
    padding: 20px 20px 0;
    display: flex;
    flex-direction: column;

    @include min-width(1024) {
        flex-direction: row;
        align-items: flex-start;
        padding: 0px 20px 0;
    }

    .photo-container {
        width: 100%;
        max-width: 360px;
        flex-shrink: 0;
        margin-bottom: 32px;
        align-self: start;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;

        @include min-width(640) {
            margin: initial;
            align-self: initial;
        }

        .photo-wrapper {
            background: $white;
            width: 100%;
            margin: 0 0 10px;
            line-height: 0;

            .image {
                width: 100%;
                height: auto;
                aspect-ratio: 1 / 1;
                object-fit: contain;
            }
        }

        .photo-disclaimer {
            font-family: $effra-font-stack;
            font-size: 0.75rem;
            line-height: 1rem;
            text-transform: uppercase;
            letter-spacing: 0.02em;
        }

        .video-link {
            font-family: $effra-font-stack;
            font-size: 0.75rem;
            line-height: 1rem;
            text-transform: uppercase;
            letter-spacing: 0.02em;
            text-decoration: underline;
        }
    }

    .details-container {
        width: 100%;

        @include min-width(1024) {
            margin-left: 40px;
        }

        .stone-parameters {
            width: 100%;
            font-size: 0.875rem;
            line-height: 1.2em;
            display: flex;
            flex-wrap: wrap;

            :slotted(.details) {
                width: 100%;
                flex-shrink: 0;
                display: flex;
                flex-wrap: wrap;

                .details-item {
                    width: 100%;
                    flex-shrink: 0;

                    @include min-width(320) {
                        width: 50%;
                    }

                    @include min-width(640) {
                        width: 33.333%;
                    }
                }

                .title {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    font-family: $parcel-font-stack;
                    color: $blue;
                }

                .tooltip-trigger {
                    color: $slate;

                    &:after {
                        font-size: 14px;
                        margin-left: 3px;
                    }
                }

                .description {
                    width: 100%;
                    margin: 0 0 15px;

                    .country-with-flag {
                        display: flex;
                        align-items: center;
                        margin-right: 10px;

                        img {
                            height: 1rem;
                            margin-right: 5px;
                        }
                    }
                }

                .link,
                .stone-brand {
                    color: $blue;
                }

                &.details-origin {
                    width: 100%;
                    display: flex;
                    flex-wrap: wrap;
                }

                &.shop-with-confidence {
                    margin-top: 1rem;
                }
            }
        }
    }
}
</style>
